import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2, Trash2, History, Plus, ChevronLeft, Check, XCircle, Sparkles, Mic, MicOff, Volume2, ShoppingCart, TrendingUp, CreditCard, Receipt, Wallet, Search, ChevronDown, ChevronUp, FileText, Camera, Image as ImageIcon, Save } from 'lucide-react';
import { dashboardApi } from '../api/dashboard';
import toast from 'react-hot-toast';
import OcrSaveModal from './OcrSaveModal';

export default function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [showQuickInput, setShowQuickInput] = useState(false);
  const [ocrUploading, setOcrUploading] = useState(false);
  const [ocrStage, setOcrStage] = useState(null); // {stage, message, progress}
  const [ocrModal, setOcrModal] = useState(null); // { ocr_data, bon_type, messageIndex } when reviewing
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = user.role === 'owner';

  // Ref to track if we should auto-send after recognition ends
  const shouldAutoSendRef = useRef(false);
  const lastTranscriptRef = useRef('');

  // Initialize speech recognition once
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false; // Disable interim for faster final result
      recognition.lang = 'id-ID';
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        lastTranscriptRef.current = transcript;
        setMessage(transcript);
        shouldAutoSendRef.current = true;
      };

      recognition.onend = () => {
        setIsListening(false);
        // Auto send immediately when recognition ends with result
        if (shouldAutoSendRef.current && lastTranscriptRef.current.trim()) {
          const textToSend = lastTranscriptRef.current.trim();
          shouldAutoSendRef.current = false;
          lastTranscriptRef.current = '';
          // Send immediately without delay
          sendVoiceMessage(textToSend);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech error:', event.error);
        setIsListening(false);
        shouldAutoSendRef.current = false;
        if (event.error === 'not-allowed') {
          toast.error('Izinkan akses mikrofon');
        } else if (event.error === 'no-speech') {
          toast.error('Tidak terdengar suara');
        }
      };

      recognitionRef.current = recognition;
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current && !isListening) {
      inputRef.current.focus();
    }
  }, [isOpen, isListening]);

  // Fast voice message sender - no delays
  const sendVoiceMessage = async (text) => {
    if (!text || loading) return;
    
    setMessage('');
    
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: `🎤 ${text}`,
      timestamp: new Date().toISOString()
    }]);
    
    setLoading(true);
    
    try {
      const response = await dashboardApi.sendAIMessage(text, sessionId, true);
      
      if (!sessionId) {
        setSessionId(response.session_id);
      }
      
      if (response.pending_action) {
        setPendingAction(response.pending_action);
      } else {
        setPendingAction(null);
      }
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.response,
        timestamp: new Date().toISOString(),
        hasPendingAction: !!response.pending_action
      }]);
      
      // Auto speak response
      if (response.response) {
        speakResponse(response.response);
      }
      
      if (response.action_executed === true) {
        toast.success('Data berhasil disimpan!');
      }
    } catch (error) {
      toast.error('Gagal mendapat respons AI');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const toggleListening = () => {
    if (!speechSupported) {
      toast.error('Browser tidak mendukung speech recognition');
      return;
    }

    if (isListening) {
      // Stop and let onend handle the auto-send
      shouldAutoSendRef.current = true;
      recognitionRef.current.stop();
    } else {
      setMessage('');
      lastTranscriptRef.current = '';
      shouldAutoSendRef.current = false;
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (e) {
        toast.error('Gagal memulai voice recognition');
      }
    }
  };

  const speakResponse = (text) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'id-ID';
      utterance.rate = 1;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    }
  };

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const data = await dashboardApi.getAIChatSessions();
      setSessions(data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSessionMessages = async (sid) => {
    try {
      const data = await dashboardApi.getAIChatMessages(sid);
      setMessages(data.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.created_at
      })));
      setSessionId(sid);
      setShowHistory(false);
      setPendingAction(null);
    } catch (error) {
      toast.error('Gagal memuat percakapan');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowHistory(false);
    setPendingAction(null);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await dashboardApi.deleteAIChatSession(sid);
      setSessions(sessions.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        startNewChat();
      }
      toast.success('Percakapan dihapus');
    } catch (error) {
      toast.error('Gagal menghapus percakapan');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar (JPEG/PNG)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Ukuran gambar maks 10MB');
      return;
    }

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';

    setOcrUploading(true);
    setOcrStage({ stage: 'reading', message: 'Membaca gambar bon...', progress: 10 });

    // Add user message bubble showing the image preview
    const previewUrl = URL.createObjectURL(file);
    setMessages(prev => [...prev, {
      role: 'user',
      content: message.trim() || 'Tolong baca bon ini',
      image_preview: previewUrl,
      timestamp: new Date().toISOString()
    }]);
    const userText = message.trim();
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('message', userText || 'Tolong ekstrak data dari bon ini');
      if (sessionId) formData.append('session_id', sessionId);

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-assistant/chat-with-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Gagal upload gambar');
      }

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              setOcrStage(data);
              if (data.stage === 'complete') finalData = data;
              if (data.stage === 'error') {
                toast.error(data.message);
                setMessages(prev => [...prev, {
                  role: 'assistant',
                  content: `Gagal: ${data.message}`,
                  timestamp: new Date().toISOString()
                }]);
              }
            } catch (e) { /* skip */ }
          }
        }
      }

      if (finalData) {
        if (finalData.session_id && !sessionId) setSessionId(finalData.session_id);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: finalData.message,
          ocr_data: finalData.data,
          bon_type: finalData.bon_type,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (err) {
      toast.error(err.message || 'Gagal memproses gambar');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Gagal: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setOcrUploading(false);
      setOcrStage(null);
    }
  };

  const sendMessage = async (customMessage = null, isVoice = false) => {
    const msgToSend = customMessage || message.trim();
    if (!msgToSend || loading) return;

    if (!customMessage) setMessage('');
    
    const displayMsg = isVoice ? `🎤 ${msgToSend}` : msgToSend;
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: displayMsg,
      timestamp: new Date().toISOString()
    }]);
    
    setLoading(true);
    
    try {
      const response = await dashboardApi.sendAIMessage(msgToSend, sessionId, isVoice);
      
      if (!sessionId) {
        setSessionId(response.session_id);
      }
      
      if (response.pending_action) {
        setPendingAction(response.pending_action);
      } else {
        setPendingAction(null);
      }
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.response,
        timestamp: new Date().toISOString(),
        hasPendingAction: !!response.pending_action
      }]);
      
      // Auto speak response if voice input
      if (isVoice && response.response) {
        speakResponse(response.response);
      }
      
      if (response.action_executed === true) {
        toast.success('Data berhasil disimpan!');
      }
    } catch (error) {
      toast.error('Gagal mendapat respons AI');
      setMessages(prev => prev.slice(0, -1));
      if (!customMessage) setMessage(msgToSend);
    } finally {
      setLoading(false);
    }
  };

  const confirmAction = async (confirm) => {
    if (!sessionId || loading) return;
    
    setLoading(true);
    
    const confirmText = confirm ? '✅ Ya, konfirmasi' : '❌ Batalkan';
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: confirmText,
      timestamp: new Date().toISOString()
    }]);
    
    try {
      const response = await dashboardApi.confirmAIAction(sessionId, confirm);
      
      setPendingAction(null);
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.response,
        timestamp: new Date().toISOString()
      }]);
      
      if (response.action_executed === true) {
        toast.success('Data berhasil disimpan!');
        speakResponse('Data berhasil disimpan');
      }
    } catch (error) {
      toast.error('Gagal memproses konfirmasi');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    // Tab to jump to next ___ placeholder
    if (e.key === 'Tab' && message.includes('___')) {
      e.preventDefault();
      const cursorPos = e.target.selectionEnd;
      const nextIdx = message.indexOf('___', cursorPos);
      if (nextIdx >= 0) {
        e.target.setSelectionRange(nextIdx, nextIdx + 3);
      } else {
        // Wrap around to first placeholder
        const firstIdx = message.indexOf('___');
        if (firstIdx >= 0) {
          e.target.setSelectionRange(firstIdx, firstIdx + 3);
        }
      }
    }
  };

  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    setMessage(e.target.value);
    // Reset height to auto to get the correct scrollHeight
    e.target.style.height = 'auto';
    // Set height based on content (max 120px)
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
  };

  const suggestedQuestions = isOwner ? [
    { label: "Ringkasan Hari Ini", icon: "📊", msg: "Berikan ringkasan kondisi bisnis hari ini" },
    { label: "Hutang Jatuh Tempo", icon: "🚨", msg: "Tampilkan hutang dan piutang yang sudah jatuh tempo" },
    { label: "Cashflow 7 Hari", icon: "💰", msg: "Proyeksi cashflow 7 hari ke depan" },
    { label: "Ranking Supplier", icon: "🏆", msg: "Ranking supplier bulan ini berdasarkan volume" },
    { label: "Simulasi Margin", icon: "📈", msg: "Simulasi: kalau fee turun Rp10/kg, laba jadi berapa?" },
    { label: "Cari Transaksi", icon: "🔍", msg: "" },
  ] : [
    { label: "Ringkasan Hari Ini", icon: "📊", msg: "Berikan ringkasan kondisi bisnis hari ini" },
    { label: "Hutang Jatuh Tempo", icon: "🚨", msg: "Tampilkan hutang yang sudah jatuh tempo" },
    { label: "Cari Transaksi", icon: "🔍", msg: "" },
    { label: "Daftar Karyawan", icon: "👥", msg: "Siapa saja karyawan aktif?" },
  ];

  const getActionTypeLabel = (type) => {
    const labels = {
      'PURCHASE': '🛒 Pembelian',
      'SALE': '💰 Penjualan',
      'EMPLOYEE': '👤 Karyawan',
      'CREATE_USER': '🔐 User Baru',
      'DELETE_USER': '🗑️ Hapus User',
      'DEBT_PAYMENT': '💳 Bayar Hutang',
      'RECEIVABLE_COLLECTION': '📥 Terima Piutang',
      'APPROVE_LEAVE': '✅ Approve Cuti',
      'REJECT_LEAVE': '❌ Tolak Cuti',
      'OPERATIONAL': '🏢 Operasional',
      'OTHER_INCOME': '💵 Pendapatan Lain',
      'CREATE_INVESTOR': '📈 Investor Baru',
      'INVESTOR_DISTRIBUTION': '💸 Distribusi Investor'
    };
    return labels[type] || type;
  };

  // Quick Input Templates for easy data entry
  const quickInputTemplates = [
    {
      category: "Input Data",
      color: "bg-emerald-50 border-emerald-200 text-emerald-700",
      items: [
        { icon: <ShoppingCart size={14} />, label: "Pembelian", template: "Catat pembelian ___kg TBS dari ___ tempat ___ harga ___/kg bayar ___" },
        { icon: <TrendingUp size={14} />, label: "Penjualan", template: "Catat penjualan ___kg ke ___ harga ___/kg" },
        { icon: <Wallet size={14} />, label: "Biaya Operasional", template: "Catat biaya operasional ___ sebesar Rp ___" },
        { icon: <Receipt size={14} />, label: "Pendapatan Lain", template: "Catat pendapatan lain ___ sebesar Rp ___" },
      ]
    },
    {
      category: "Pembayaran",
      color: "bg-blue-50 border-blue-200 text-blue-700",
      items: [
        { icon: <CreditCard size={14} />, label: "Bayar Hutang", template: "Bayar hutang ke ___ sebesar Rp ___" },
        { icon: <CreditCard size={14} />, label: "Terima Piutang", template: "Terima piutang dari ___ sebesar Rp ___" },
      ]
    },
    {
      category: "Cari Data",
      color: "bg-amber-50 border-amber-200 text-amber-700",
      items: [
        { icon: <Search size={14} />, label: "Cari Hutang", template: "Cari hutang ___" },
        { icon: <Search size={14} />, label: "Cari Pembelian", template: "Cari pembelian ___" },
        { icon: <Search size={14} />, label: "Cari Penjualan", template: "Cari penjualan ___" },
        { icon: <Search size={14} />, label: "Cari Karyawan", template: "Cari data karyawan ___" },
        { icon: <FileText size={14} />, label: "Pengeluaran Owner", template: "Cari pengeluaran owner" },
      ]
    },
  ];

  const handleTemplateClick = (template) => {
    setMessage(template);
    setShowQuickInput(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // Auto-resize textarea to fit template
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
        // Select the first ___ placeholder for easy editing
        const idx = template.indexOf('___');
        if (idx >= 0) {
          inputRef.current.setSelectionRange(idx, idx + 3);
        }
      }
    }, 50);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => {
          setIsOpen(true);
          if (sessions.length === 0) loadSessions();
        }}
        className={`fixed bottom-20 right-6 z-50 p-4 rounded-full shadow-lg transition-all duration-300 ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        } bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white`}
        data-testid="ai-assistant-button"
      >
        <MessageCircle size={24} />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-20 right-6 z-50 transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
        }`}
      >
        <div className="w-[420px] h-[650px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-stone-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {showHistory ? (
                <button onClick={() => setShowHistory(false)} className="p-1 hover:bg-white/20 rounded">
                  <ChevronLeft size={20} />
                </button>
              ) : (
                <div className="p-2 bg-white/20 rounded-lg">
                  <Bot size={20} />
                </div>
              )}
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  {showHistory ? 'Riwayat Chat' : 'AI Assistant'}
                  {isOwner && !showHistory && <Sparkles size={14} className="text-yellow-300" />}
                  {speechSupported && !showHistory && <Mic size={14} className="text-green-300" />}
                </h3>
                {showHistory ? (
                  <p className="text-xs text-blue-100">{sessions.length} percakapan</p>
                ) : (
                  <p className="text-xs text-blue-100">{isOwner ? 'Full Access + Voice Command' : 'Powered by GPT-4o'}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!showHistory && (
                <>
                  <button onClick={() => { setShowHistory(true); loadSessions(); }} className="p-2 hover:bg-white/20 rounded-lg" title="Riwayat">
                    <History size={18} />
                  </button>
                  <button onClick={startNewChat} className="p-2 hover:bg-white/20 rounded-lg" title="Chat Baru">
                    <Plus size={18} />
                  </button>
                </>
              )}
              <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/20 rounded-lg">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Content */}
          {showHistory ? (
            <div className="flex-1 overflow-y-auto p-4">
              {loadingSessions ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="animate-spin text-slate-400" size={32} />
                </div>
              ) : sessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <History size={48} className="mb-3 opacity-50" />
                  <p>Belum ada riwayat chat</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <div
                      key={session.session_id}
                      onClick={() => loadSessionMessages(session.session_id)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                        sessionId === session.session_id ? 'bg-blue-50 border border-blue-200' : 'bg-stone-50 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <p className="text-sm text-slate-700 line-clamp-2 flex-1">{session.preview}</p>
                        <button onClick={(e) => deleteSession(session.session_id, e)} className="p-1.5 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{session.message_count} pesan</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center px-4">
                    <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full mb-4">
                      <Bot size={36} className="text-blue-600" />
                    </div>
                    <h4 className="font-bold text-slate-800 mb-2">Halo! Saya AI Assistant</h4>
                    <p className="text-sm text-slate-500 mb-2">
                      {isOwner ? 'Akses penuh ke semua fitur + perintah suara!' : 'Tanyakan apapun tentang keuangan bisnis'}
                    </p>
                    {isOwner && (
                      <div className="flex gap-2 mb-4">
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                          <Sparkles size={12} /> Full Access
                        </span>
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex items-center gap-1">
                          <Mic size={12} /> Voice
                        </span>
                      </div>
                    )}
                    <div className="w-full grid grid-cols-2 gap-2">
                      {suggestedQuestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            if (q.msg) { sendMessage(q.msg); }
                            else { setMessage('Cari transaksi '); inputRef.current?.focus(); }
                          }}
                          className="p-2.5 text-left text-sm bg-white border border-stone-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors flex items-center gap-2"
                          data-testid={`quick-cmd-${i}`}
                        >
                          <span className="text-lg">{q.icon}</span>
                          <span className="font-medium text-slate-700">{q.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div key={index} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                      <div className={`p-2 rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0 ${
                        msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {msg.role === 'user' ? <User size={16} /> : <Bot size={16} />}
                      </div>
                      <div className={`max-w-[80%] ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                        {msg.image_preview && (
                          <img src={msg.image_preview} alt="Bon" className="max-w-[200px] rounded-lg mb-1 border border-stone-200" data-testid="msg-image-preview" />
                        )}
                        <div className={`px-4 py-2.5 rounded-2xl text-sm ${
                          msg.role === 'user' 
                            ? 'bg-blue-600 text-white rounded-br-md' 
                            : 'bg-white text-slate-800 border border-stone-200 rounded-bl-md shadow-sm'
                        }`}>
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        {/* OCR Save Button — appears on assistant messages with ocr_data */}
                        {msg.role === 'assistant' && msg.ocr_data && msg.bon_type && !msg.ocr_saved && (
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              onClick={() => setOcrModal({ ocr_data: msg.ocr_data, bon_type: msg.bon_type, messageIndex: index })}
                              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg shadow-sm transition-colors ${
                                msg.bon_type === 'purchase' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'
                              }`}
                              data-testid={`ocr-review-save-btn-${index}`}
                            >
                              <Save size={12} />
                              Tinjau & Simpan {msg.bon_type === 'purchase' ? 'Pembelian' : 'Penjualan'}
                            </button>
                          </div>
                        )}
                        {msg.role === 'assistant' && msg.ocr_saved && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full" data-testid={`ocr-saved-badge-${index}`}>
                            <Check size={12} /> Tersimpan{msg.ocr_invoice ? ` · ${msg.ocr_invoice}` : ''}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1 px-2">
                          <span className="text-xs text-slate-400">{formatTime(msg.timestamp)}</span>
                          {msg.role === 'assistant' && (
                            <button onClick={() => speakResponse(msg.content)} className="text-slate-400 hover:text-blue-500" title="Dengarkan">
                              <Volume2 size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {/* OCR Progress Indicator */}
                {ocrUploading && ocrStage && (
                  <div className="flex gap-2" data-testid="ocr-progress">
                    <div className="p-2 rounded-full h-8 w-8 flex items-center justify-center bg-emerald-100 text-emerald-700">
                      <ImageIcon size={16} />
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[80%]">
                      <p className="text-sm text-emerald-800 font-medium flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        {ocrStage.message}
                      </p>
                      <div className="mt-2 h-1.5 bg-emerald-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 transition-all duration-300"
                          style={{ width: `${ocrStage.progress || 0}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-emerald-600 mt-1 capitalize">{ocrStage.stage}</p>
                    </div>
                  </div>
                )}
                {loading && (
                  <div className="flex gap-2">
                    <div className="p-2 rounded-full h-8 w-8 flex items-center justify-center bg-slate-200 text-slate-600">
                      <Bot size={16} />
                    </div>
                    <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-2">
                        <Loader2 className="animate-spin" size={16} />
                        <span className="text-sm text-slate-500">Memproses...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Pending Action Confirmation */}
              {pendingAction && !loading && (
                <div className="px-4 py-3 border-t border-stone-200 bg-amber-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={16} className="text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">
                      Konfirmasi {getActionTypeLabel(pendingAction.type)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => confirmAction(true)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium" data-testid="ai-confirm-yes">
                      <Check size={16} /> Ya, Simpan
                    </button>
                    <button onClick={() => confirmAction(false)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 text-sm font-medium" data-testid="ai-confirm-no">
                      <XCircle size={16} /> Batalkan
                    </button>
                  </div>
                </div>
              )}

              {/* Voice Recording Indicator */}
              {isListening && (
                <div className="px-4 py-2 border-t border-blue-200 bg-blue-50">
                  <div className="flex items-center justify-center gap-2">
                    <div className="relative">
                      <Mic size={20} className="text-blue-600" />
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                    </div>
                    <span className="text-sm text-blue-700">Bicara sekarang...</span>
                  </div>
                </div>
              )}

              {/* Quick Input Templates Panel */}
              {showQuickInput && !loading && !pendingAction && (
                <div className="border-t border-stone-200 bg-white max-h-[240px] overflow-y-auto" data-testid="quick-input-panel">
                  {quickInputTemplates.map((group, gi) => (
                    <div key={gi} className="px-3 py-2">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{group.category}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {group.items.map((item, ii) => (
                          <button
                            key={ii}
                            onClick={() => handleTemplateClick(item.template)}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border rounded-lg hover:shadow-sm transition-all ${group.color}`}
                            data-testid={`quick-tpl-${gi}-${ii}`}
                          >
                            {item.icon}
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="p-3 border-t border-stone-200 bg-white">
                <div className="flex gap-2 items-end">
                  {isOwner && (
                    <button
                      onClick={() => setShowQuickInput(!showQuickInput)}
                      disabled={loading || !!pendingAction}
                      className={`p-2 rounded-full transition-all flex-shrink-0 ${
                        showQuickInput
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                      } disabled:opacity-50`}
                      title="Template Input Cepat"
                      data-testid="quick-input-toggle"
                    >
                      {showQuickInput ? <ChevronDown size={18} /> : <Plus size={18} />}
                    </button>
                  )}
                  {speechSupported && (
                    <button
                      onClick={toggleListening}
                      disabled={loading || !!pendingAction || ocrUploading}
                      className={`p-2 rounded-full transition-all flex-shrink-0 ${
                        isListening 
                          ? 'bg-red-500 text-white animate-pulse' 
                          : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                      } disabled:opacity-50`}
                      title={isListening ? 'Stop & Kirim' : 'Bicara'}
                      data-testid="ai-voice-button"
                    >
                      {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                    </button>
                  )}
                  {/* Image Upload (OCR Bon) */}
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="ai-image-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading || !!pendingAction || ocrUploading}
                    className="p-2 rounded-full bg-stone-100 text-stone-500 hover:bg-emerald-100 hover:text-emerald-700 disabled:opacity-50 transition-all flex-shrink-0"
                    title="Upload foto bon (OCR)"
                    data-testid="ai-image-button"
                  >
                    <Camera size={16} />
                  </button>
                  <textarea
                    ref={inputRef}
                    value={message}
                    onChange={handleTextareaChange}
                    onKeyDown={handleKeyPress}
                    placeholder={isListening ? "Bicara..." : (isOwner ? "Ketik atau klik + untuk template..." : "Ketik pertanyaan...")}
                    className="flex-1 px-3 py-2 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none overflow-hidden min-h-[38px] max-h-[120px]"
                    disabled={loading || !!pendingAction}
                    rows={1}
                    style={{ height: '38px' }}
                    data-testid="ai-chat-input"
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!message.trim() || loading || !!pendingAction}
                    className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0"
                    data-testid="ai-send-button"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {/* OCR Review & Save Modal */}
      {ocrModal && (
        <OcrSaveModal
          ocrData={ocrModal.ocr_data}
          bonType={ocrModal.bon_type}
          onClose={() => setOcrModal(null)}
          onSaved={(result) => {
            // mark the message as saved so the button disappears
            setMessages((prev) => prev.map((m, i) => (
              i === ocrModal.messageIndex
                ? { ...m, ocr_saved: true, ocr_invoice: result?.invoice_number || null }
                : m
            )));
          }}
        />
      )}
    </>
  );
}
